import React, { useState, useRef, useEffect } from "react";
import api, { pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Empty } from "@/components/common";
import { toast } from "sonner";
import { UploadCloud, FileAudio, Loader2, RefreshCw, Check, ArrowRight, Type, AlertTriangle, Gauge, HardDriveDownload } from "lucide-react";

function fmt(s) { return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`; }
const meaningfulLen = (t) => (t || "").replace(/[^\p{L}\p{N}]/gu, "").length;

export default function ReviewStep({ meeting, transcripts, refresh, setActive, mid }) {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [drag, setDrag] = useState(false);
  const [lang, setLang] = useState("");
  const fileRef = useRef(null);
  const active = transcripts.find((t) => t.active) || transcripts[0];
  const [showScore, setShowScore] = useState(false);
  const [refText, setRefText] = useState("");
  const [acc, setAcc] = useState(null);
  const [audio, setAudio] = useState(null);

  useEffect(() => {
    let on = true;
    api.get(`/meetings/${mid}/audio-status`).then((r) => on && setAudio(r.data)).catch(() => {});
    return () => { on = false; };
  }, [mid, transcripts.length]);

  const scoreAccuracy = async () => {
    if (refText.trim().length < 10) { toast.error("Paste the correct transcript to compare"); return; }
    try {
      const r = await api.post(`/meetings/${mid}/score-accuracy`, { reference_text: refText });
      setAcc(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Scoring failed"); }
  };

  const retranscribeSaved = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (lang) fd.append("language", lang);
      const r = await api.post(`/meetings/${mid}/retranscribe-saved`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.job_id) await pollJob(r.data.job_id);
      toast.success("Re-transcribed from saved audio");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "No saved audio available"); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Audio exceeds 25 MB. Please split the file."); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("engine", "auto");
    if (lang) fd.append("language", lang);
    try {
      const res = await api.post(`/meetings/${mid}/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      if (res.data.job_id) await pollJob(res.data.job_id);
      toast.success("Audio transcribed");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Transcription failed"); }
    finally { setBusy(false); }
  };

  const submitText = async () => {
    if (text.trim().length < 20) { toast.error("Paste a longer transcript"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("text", text);
    fd.append("engine", "manual");
    try {
      await api.post(`/meetings/${mid}/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Transcript added");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const editSeg = async (idx, field, value) => {
    await api.put(`/transcripts/${active.id}/segment`, { index: idx, [field]: value });
    refresh();
  };
  const activate = async (tid) => { await api.post(`/transcripts/${tid}/activate`); toast.success("Version activated"); refresh(); };

  return (
    <div className="space-y-6">
      {transcripts.length === 0 ? (
        <Tabs defaultValue="upload">
          <TabsList className="grid grid-cols-2 w-full max-w-md">
            <TabsTrigger value="upload" data-testid="tab-upload"><FileAudio size={14} className="mr-1.5" /> Upload audio</TabsTrigger>
            <TabsTrigger value="paste" data-testid="tab-paste"><Type size={14} className="mr-1.5" /> Paste transcript</TabsTrigger>
          </TabsList>
          <TabsContent value="upload" className="mt-4">
            <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`rounded-xl border-2 border-dashed p-12 text-center cursor-pointer transition ${drag ? "border-[#29abe2] bg-[#29abe2]/5" : "border-border hover:border-[#29abe2]/50"}`}
              data-testid="audio-dropzone">
              <input ref={fileRef} type="file" accept=".mp3,.m4a,.wav,.mp4,.webm,.mpeg,.mpga,audio/*,video/mp4" hidden onChange={(e) => upload(e.target.files[0])} />
              {busy ? <div className="flex flex-col items-center gap-3 text-[#29abe2]"><Loader2 className="animate-spin" size={28} /><span className="text-sm">Transcribing with Whisper… this can take a minute.</span></div>
                : <><UploadCloud size={32} className="mx-auto text-[#29abe2]" /><div className="font-medium mt-3">Drop meeting audio or click to browse</div><div className="text-sm text-muted-foreground mt-1">MP3, M4A, WAV, MP4, WEBM · up to 25 MB · auto-transcribed with SAP glossary boost</div></>}
            </div>
          </TabsContent>
          <TabsContent value="paste" className="mt-4 space-y-3">
            <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the meeting transcript here (one paragraph per speaker turn)…" data-testid="paste-transcript" />
            <Button onClick={submitText} disabled={busy} className="gap-2">{busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Use this transcript</Button>
          </TabsContent>
        </Tabs>
      ) : (
        <>
          {active && active.segments?.length > 0 && active.segments.filter((s) => meaningfulLen(s.text) >= 2).length <= Math.max(1, Math.floor(active.segments.length * 0.1)) && (
            <div className="rounded-xl border border-red-400 bg-red-500/10 p-4 flex items-start gap-3" data-testid="empty-transcript-banner">
              <AlertTriangle className="text-red-500 mt-0.5" size={18} />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-red-600">This transcript has almost no words</div>
                <div className="text-red-600/80">The audio was likely silent, very low quality, or in a language the engine wasn't set to. If the meeting was in Telugu/Tamil/Hindi, pick that language below and <b>Re-transcribe</b> with the original audio — it will now use the Indic engine. You can also paste the transcript text.</div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Transcript versions:</span>
              {transcripts.map((t) => (
                <button key={t.id} onClick={() => activate(t.id)} data-testid={`version-${t.version}`}
                  className={`text-xs px-2.5 py-1 rounded-lg border ${t.active ? "bg-[#0f2557] text-white border-[#0f2557] dark:bg-[#29abe2] dark:border-[#29abe2]" : "hover:border-[#29abe2]"}`}>
                  v{t.version} · {t.engine} {t.active && "✓"}
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="asr-language"
                className="h-9 rounded-md border bg-background text-sm px-2" title="Transcription language">
                <option value="">Auto-detect</option>
                <option value="te">Telugu (Sarvam)</option>
                <option value="ta">Tamil (Sarvam)</option>
                <option value="hi">Hindi (Sarvam)</option>
                <option value="kn">Kannada (Sarvam)</option>
                <option value="ml">Malayalam (Sarvam)</option>
                <option value="en">English (Whisper)</option>
              </select>
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="gap-2" data-testid="retranscribe">
                {busy ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />} Re-transcribe
              </Button>
              <input ref={fileRef} type="file" accept="audio/*,video/mp4,.mp3,.m4a,.wav,.webm" hidden onChange={(e) => upload(e.target.files[0])} />
              <Button onClick={() => setActive("clarify")} className="gap-2" data-testid="go-clarify">Next: Analyze <ArrowRight size={16} /></Button>
            </div>
          </div>

          {active && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-4 rounded-xl border bg-card px-4 py-2.5 text-sm" data-testid="transcript-stats">
                <div><span className="text-muted-foreground text-xs">Accuracy </span><b className="text-[#0f2557] dark:text-[#29abe2]">{active.quality_report?.accuracy_pct ?? "—"}%</b></div>
                <div><span className="text-muted-foreground text-xs">Coverage </span><b>{active.quality_report?.coverage_pct ?? "—"}%</b></div>
                <div><span className="text-muted-foreground text-xs">Words/min </span><b>{active.quality_report?.wpm ?? "—"}</b></div>
                <div><span className="text-muted-foreground text-xs">Languages </span><b>{(active.quality_report?.languages || []).map((l) => l.lang).join(", ") || "—"}</b></div>
              </div>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setShowScore((v) => !v)} data-testid="check-accuracy-btn"><Gauge size={14} /> Check accuracy</Button>
              {audio?.available && (
                <>
                  <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={retranscribeSaved} disabled={busy} data-testid="retranscribe-saved-btn"><HardDriveDownload size={14} /> Re-transcribe from saved audio</Button>
                  <span className="text-xs text-muted-foreground">Audio stored · expires {audio.expires_at ? new Date(audio.expires_at).toLocaleDateString() : "—"}</span>
                </>
              )}
            </div>
          )}
          {showScore && (
            <div className="rounded-xl border bg-card p-4 space-y-2" data-testid="accuracy-panel">
              <div className="text-sm font-semibold text-[#0f2557] dark:text-[#29abe2]">Score transcription accuracy against a reference</div>
              <p className="text-xs text-muted-foreground">Paste the known-correct transcript (ground truth). We compute word accuracy (1 − word error rate) versus the current transcript — ideal for assessing Sarvam on a Telugu recording.</p>
              <Textarea value={refText} onChange={(e) => setRefText(e.target.value)} rows={3} placeholder="Paste the correct transcript here…" data-testid="reference-text" />
              <div className="flex items-center gap-3 flex-wrap">
                <Button size="sm" onClick={scoreAccuracy} data-testid="score-btn">Score accuracy</Button>
                {acc && <span className="text-sm" data-testid="accuracy-result"><b className="text-[#0f2557] dark:text-[#29abe2] text-base">{acc.accuracy_pct}%</b> accurate · WER {acc.wer} · {acc.words_transcribed}/{acc.words_reference} words</span>}
              </div>
            </div>
          )}

          <div className="rounded-xl border bg-card p-6 max-h-[560px] overflow-y-auto" data-testid="transcript-view">
            {active.segments.map((s, i) => (
              <div key={i} className="group flex gap-4 py-2 border-b border-border/50 last:border-0">
                <div className="w-28 shrink-0">
                  <input defaultValue={s.speaker} onBlur={(e) => e.target.value !== s.speaker && editSeg(i, "speaker", e.target.value)}
                    className="w-full bg-transparent text-xs font-semibold text-[#0f2557] dark:text-[#29abe2] outline-none focus:bg-secondary rounded px-1" />
                  <div className="text-[10px] text-muted-foreground font-mono px-1">{fmt(s.start)}</div>
                </div>
                <textarea defaultValue={s.text} onBlur={(e) => e.target.value !== s.text && editSeg(i, "text", e.target.value)} rows={1}
                  className={`flex-1 bg-transparent text-sm text-foreground outline-none focus:bg-secondary rounded px-2 py-0.5 resize-none ${s.confidence < 0.6 ? "underline decoration-wavy decoration-[#c9a227]" : ""}`}
                  style={{ minHeight: "1.5rem" }} />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Low-confidence words are underlined. Click any speaker or text to edit inline — changes are saved automatically.</p>
        </>
      )}
    </div>
  );
}
